import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, useFlash } from '../../ui/kit.js';
import { useBuilding, day, clock, nextId } from '../../state/building.js';

import PoolDeck from '../../media/images/modules/area-pool-deck.jpg';
import FireExit from '../../media/images/modules/inspect-exit.jpg';
import Garage from '../../media/images/modules/inspect-garage.jpg';
import UnitPhoto from '../../media/images/modules/inspect-unit.jpg';

const TEMPLATES = {
    'Daily pool deck': [
        'Pool gate self-closes and latches',
        'Chlorine and pH within range, logged',
        'Life ring and shepherd hook in place',
        'No glass on deck or in cabanas',
        'Drain covers secure, no cracks',
        'Pool rules and depth signs legible'
    ],
    'Fire exit walk': [
        'Stairwell A doors close and latch on every floor',
        'Stairwell B clear of stored items',
        'Exit signs lit, battery test passes',
        'Emergency lights on in stair landings',
        'Fire extinguishers charged and tagged',
        'Standpipe cabinets unobstructed'
    ],
    'Garage security round': [
        'Entry gate arms and loop detectors working',
        'All P1 to P3 cameras recording',
        'Stair and elevator lobby doors locked',
        'Emergency call stations answer',
        'No unregistered vehicles in resident spaces',
        'Lighting working in every bay'
    ],
    'Unit move-out inspection': [
        'Walls free of holes beyond normal wear',
        'Floors undamaged, no water staining',
        'Appliances clean and working',
        'Smoke detectors present and test OK',
        'All fobs, keys and remotes returned',
        'Balcony glass and railings intact'
    ]
};

const TEMPLATE_PHOTOS = {
    'Daily pool deck': PoolDeck,
    'Fire exit walk': FireExit,
    'Garage security round': Garage,
    'Unit move-out inspection': UnitPhoto
};

const INSPECTORS = ['Luis M.', 'Dana K.', 'Omar R.', 'Carlos T.'];

/* Inspections and Checklists: a walk-through where each item passes or
   fails, and every failure becomes a work order on submit. */
function InspectionsScreen() {
    const { add } = useBuilding();
    const [template, setTemplate] = useState('Daily pool deck');
    const [results, setResults] = useState({});
    const [inspector, setInspector] = useState(INSPECTORS[0]);
    const [history, setHistory] = useState([
        { id: 1, template: 'Fire exit walk', inspector: 'Omar R.', date: day(-1), time: '7:40 AM', passed: 6, total: 6 },
        { id: 2, template: 'Daily pool deck', inspector: 'Carlos T.', date: day(-1), time: '8:15 AM', passed: 5, total: 6 },
        { id: 3, template: 'Garage security round', inspector: 'Dana K.', date: day(-2), time: '11:05 PM', passed: 6, total: 6 }
    ]);
    const [flash, notify] = useFlash();

    const items = TEMPLATES[template];
    const answered = items.filter(item => results[item]).length;
    const pct = Math.round((answered / items.length) * 100);

    const pick = name => {
        setTemplate(name);
        setResults({});
    };

    const mark = (item, value) => setResults(r => ({ ...r, [item]: r[item] === value ? undefined : value }));

    const submit = () => {
        if (answered < items.length) return notify(`Mark all ${items.length} items before submitting. ${items.length - answered} left.`);

        const failed = items.filter(item => results[item] === 'fail');
        failed.forEach(item => add('workOrders', {
            unit: 'Common area', title: item, category: 'Inspection', priority: 'Normal', status: 'New', assignee: '', opened: day(0), entry: false
        }));

        setHistory(list => [{
            id: nextId(), template, inspector, date: day(0), time: clock(), passed: items.length - failed.length, total: items.length
        }, ...list]);
        setResults({});
        notify(failed.length
            ? `${template} submitted. ${failed.length} failed ${failed.length === 1 ? 'item became a work order' : 'items became work orders'} on the maintenance board.`
            : `${template} submitted. Everything passed.`);
    };

    const today = history.filter(h => h.date === day(0));

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Inspections" title="Inspections and checklists" />

            <Kpis items={[
                { label: 'Completed today', value: today.length, tone: 'info' },
                { label: 'Items failed today', value: today.reduce((n, h) => n + h.total - h.passed, 0), tone: 'alert' },
                { label: 'Pass rate', value: `${Math.round((history.reduce((n, h) => n + h.passed, 0) / Math.max(history.reduce((n, h) => n + h.total, 0), 1)) * 100)}%`, tone: 'ok' },
                { label: 'Templates', value: Object.keys(TEMPLATES).length }
            ]} />

            {flash}

            <div className="tiles" role="group" aria-label="Inspection template">
                {Object.keys(TEMPLATES).map(name => (
                    <button type="button" className="tile tile--photo" key={name} aria-pressed={template === name} onClick={() => pick(name)}>
                        <img src={TEMPLATE_PHOTOS[name]} alt="" loading="lazy" />
                        {name}
                    </button>
                ))}
            </div>

            <div className="split section-gap">
                <Panel className="panel--accent">
                    <h4>{template}</h4>
                    <Field label="Inspector">
                        <select className="form-control" value={inspector} onChange={e => setInspector(e.target.value)}>
                            {INSPECTORS.map(name => <option key={name}>{name}</option>)}
                        </select>
                    </Field>
                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                        <span className="row__meta">{answered} of {items.length} checked</span>
                        <span className="row__meta">{pct}%</span>
                    </div>
                    <div className="meter">
                        <span className="meter__fill" style={{ width: `${pct}%` }} />
                    </div>
                    <button type="button" className="btn btn-primary" onClick={submit}>Submit inspection</button>
                </Panel>

                <ul className="checks">
                    {items.map(item => {
                        const result = results[item];
                        return (
                            <li key={item}>
                                <div className={`check${result === 'pass' ? ' check--on' : ''}${result === 'fail' ? ' check--fail' : ''}`}>
                                    <span className="check__box">{result === 'pass' ? '✓' : result === 'fail' ? '✕' : ''}</span>
                                    <span className="check__text" style={{ flex: 1 }}>{item}</span>
                                    <button type="button" className={`btn btn-sm ${result === 'pass' ? 'btn-primary' : 'btn-outline-light'}`} aria-pressed={result === 'pass'} onClick={() => mark(item, 'pass')}>
                                        Pass
                                    </button>
                                    <button type="button" className={`btn btn-sm ${result === 'fail' ? 'btn-primary' : 'btn-outline-light'}`} aria-pressed={result === 'fail'} onClick={() => mark(item, 'fail')}>
                                        Fail
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="section-gap stack">
                <h4>History</h4>
                <ul className="rows">
                    {history.map(h => (
                        <li className="row" key={h.id}>
                            <div className="row__main">
                                <span className="row__title">{h.template}</span>
                                <span className="row__meta">{h.inspector} · {h.date} at {h.time} · {h.passed} of {h.total} passed</span>
                            </div>
                            <div className="row__end">
                                {h.passed === h.total
                                    ? <Chip tone="ok">All clear</Chip>
                                    : <Chip tone="alert">{h.total - h.passed} failed</Chip>}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default InspectionsScreen;
